import type { SceneNode, SceneDocument } from '@/lib/scene-document';
import type { ProductVariantSearchResultDto } from '@/apis/backend/generated/models';

/**
 * Floating contextual toolbar that hovers above the current selection — the
 * Figma/Canva "quick actions" affordance. Complements (does not replace) the
 * right-side properties panel: the most common edits are one click away, on the
 * canvas, without crossing to a side panel.
 *
 * Mounted as a sibling of the canvas (and InlineTextOverlay) inside the editor
 * container, so it positions in screen space using the same
 * worldBounds*zoom + panOffset formula. Hidden while dragging/resizing/cropping
 * or editing text so it never fights the gesture.
 */
import { useState } from 'react';
import { useStore } from 'zustand';
import { useTranslation } from 'react-i18next';
import { Iconify } from '@/components/iconify';
import { SOCIAL_PLATFORMS } from '@/lib/social-icons';
import { useOrganization } from '@/context/organization-context';
import { ProductPickerModal } from '@/components/email-generation/ProductPickerModal';
import { socialIconUrl, type SocialPlatform } from '@nexmail/shared-util/email-builder';
import { useBrandsControllerGetClientBrandProfile } from '@/apis/backend/generated/brands/brands';

import ShareIcon from '@mui/icons-material/Share';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FlipToBackIcon from '@mui/icons-material/FlipToBack';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FlipToFrontIcon from '@mui/icons-material/FlipToFront';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AlignVerticalTopIcon from '@mui/icons-material/AlignVerticalTop';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import VerticalAlignCenterIcon from '@mui/icons-material/VerticalAlignCenter';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';
import AlignVerticalBottomIcon from '@mui/icons-material/AlignVerticalBottom';
import AlignHorizontalRightIcon from '@mui/icons-material/AlignHorizontalRight';
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter';
import { Box, Paper, Tooltip, Divider, Popover, IconButton } from '@mui/material';

import { getNodeAABB } from './canvaskit-hit-test';
import { generateNodeId } from './canvaskit-mutations';
import { HANDLE_SIZE, ROTATION_HANDLE_OFFSET } from './canvaskit-transform';
import {
  findProductCardRoot,
  pickedProductFromDto,
  buildProductSwapPatches,
} from './product-binding-web';

import type { AlignEdge } from './canvaskit-align';
import type { CanvasEditorStore } from './canvaskit-editor-store';

// A "social row" is an auto-layout FRAME whose children include recognizable
// social-icon IMAGE nodes (named after a platform or sourced from the social
// icon CDN). We surface a one-click "add social icon" affordance on these so a
// user doesn't have to duplicate-and-reswap an icon by hand.
function findSocialRowId(doc: SceneDocument, single: SceneNode | null): string | null {
  const isSocialIcon = (n: SceneNode | undefined): boolean => {
    if (!n || n.type !== 'IMAGE') return false;
    if ((n.imageSrc ?? '').includes('/static/social/')) return true;
    const name = (n.name ?? '').toLowerCase();
    return name.includes('social') || SOCIAL_PLATFORMS.some((p) => name.includes(p.key));
  };
  const frameHasSocial = (f: SceneNode | undefined): boolean =>
    !!f && f.type === 'FRAME' && (f.children ?? []).some((id) => isSocialIcon(doc.nodes[id]));
  if (!single) return null;
  if (frameHasSocial(single)) return single.id;
  if (single.type === 'IMAGE' && single.parentId && frameHasSocial(doc.nodes[single.parentId])) {
    return single.parentId;
  }
  return null;
}

// Derive a new icon's src by reusing an existing sibling's URL family and
// swapping the platform slug (e.g. `.../static/social/instagram.png` →
// `.../static/social/threads.png`). Keeps the new icon in the same visual set
// as the ones already in the row, whatever source the row was built from.
function deriveSrcFromSibling(src: string | undefined, target: SocialPlatform): string | null {
  if (!src) return null;
  for (const { key } of SOCIAL_PLATFORMS) {
    const re = new RegExp(`(^|[/_-])${key}([._-]|$)`, 'i');
    if (re.test(src)) return src.replace(re, (_m, a, b) => `${a}${target}${b}`);
  }
  return null;
}

// Clearance above the selection so the toolbar floats above the rotation
// handle (which sits ROTATION_HANDLE_OFFSET above top-center) rather than on it.
const GAP_ABOVE = ROTATION_HANDLE_OFFSET + HANDLE_SIZE + 12;

export function SelectionToolbar({ store }: { store: CanvasEditorStore }) {
  const { t } = useTranslation('canvas');
  const { activeOrganization, activeClient } = useOrganization();
  const orgId = activeOrganization?.id ?? '';
  const clientId = activeClient?.id ?? '';
  const { data: brandProfile } = useBrandsControllerGetClientBrandProfile(orgId, clientId, {
    query: { enabled: !!orgId && !!clientId },
  });
  const socialLinks = brandProfile?.data?.socialLinks as
    | Record<string, string | undefined>
    | undefined;
  const socialIcons = brandProfile?.data?.socialIcons as
    | Record<string, string | undefined>
    | undefined;
  const selectedIds = useStore(store, (s) => s.selectedIds);
  const doc = useStore(store, (s) => s.doc);
  const zoom = useStore(store, (s) => s.zoom);
  const panOffset = useStore(store, (s) => s.panOffset);
  const interactionMode = useStore(store, (s) => s.interactionMode);
  const editingTextId = useStore(store, (s) => s.editingTextId);
  const croppingNodeId = useStore(store, (s) => s.croppingNodeId);
  const linkZonesVisible = useStore(store, (s) => s.linkZonesVisible);

  const [alignAnchor, setAlignAnchor] = useState<HTMLElement | null>(null);
  const [socialAnchor, setSocialAnchor] = useState<HTMLElement | null>(null);
  const [productPickerOpen, setProductPickerOpen] = useState(false);

  // Hide during active gestures / text edit / crop / link-zone editing so it
  // never jitters, overlaps, or offers actions (delete) that break invariants.
  if (
    selectedIds.size === 0 ||
    interactionMode !== 'idle' ||
    editingTextId ||
    croppingNodeId ||
    linkZonesVisible
  ) {
    return null;
  }

  // Union AABB of the selection in scene space.
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const id of selectedIds) {
    if (!doc.nodes[id]) continue;
    const a = getNodeAABB(doc, id);
    minX = Math.min(minX, a.x);
    minY = Math.min(minY, a.y);
    maxX = Math.max(maxX, a.x + a.width);
    maxY = Math.max(maxY, a.y + a.height);
  }
  if (!isFinite(minX)) return null;

  // Screen position: centered on the selection. Default above (clearing the
  // rotation handle); flip below the selection's bottom edge when there isn't
  // room above, so it never re-collides with the handle near the top edge.
  const screenLeft = ((minX + maxX) / 2) * zoom + panOffset.x;
  const screenTop = minY * zoom + panOffset.y;
  const screenBottom = maxY * zoom + panOffset.y;
  const placeBelow = screenTop - GAP_ABOVE < 48;

  const single = selectedIds.size === 1 ? doc.nodes[[...selectedIds][0]] : null;
  const isContainer = !!single && (single.type === 'GROUP' || single.type === 'FRAME');
  const canGroup = selectedIds.size >= 2;
  const canDistribute = selectedIds.size >= 3;
  const socialRowId = findSocialRowId(doc, single);
  // Selection sits in/over a product card (bound block or unbound structural
  // card) → offer a one-click "Replace product" that rebinds the whole card.
  const productCardRootId = single ? findProductCardRoot(doc, single.id) : null;

  // Float / Dock: a flow child of an auto-layout container can be popped out to
  // free (absolute) positioning so it sits over a sibling (e.g. text over an
  // image); a floating node can be docked back into the stack. Hidden for
  // elements that aren't inside an auto-layout container and aren't floating.
  const floatParent = single?.parentId ? doc.nodes[single.parentId] : undefined;
  const isFloating = single?.layoutPositioning === 'ABSOLUTE';
  const canFloat =
    !!single &&
    (floatParent?.layoutMode === 'VERTICAL' ||
      floatParent?.layoutMode === 'HORIZONTAL' ||
      isFloating);

  const act = (fn: (s: ReturnType<CanvasEditorStore['getState']>) => void) => () =>
    fn(store.getState());

  // Insert a new platform icon into the selected social row. We clone the row's
  // last existing icon so size/fills/constraints match, then swap the glyph,
  // name and alt. Everything brand-specific comes from the brand profile: the
  // link is the brand's saved socialLinks URL, and the icon prefers a brand
  // custom socialIcons override, then the existing row's icon family (so it
  // matches the others), then the canonical CDN set. Append order places it as
  // the last icon; the auto-layout pass reflows the row.
  const addSocialIcon = (platform: SocialPlatform, label: string) => {
    if (!socialRowId) return;
    const s = store.getState();
    const frame = s.doc.nodes[socialRowId];
    if (!frame) return;
    const template = [...(frame.children ?? [])]
      .map((id) => s.doc.nodes[id])
      .reverse()
      .find((n): n is SceneNode => !!n && n.type === 'IMAGE');
    const imageSrc =
      socialIcons?.[platform] ||
      deriveSrcFromSibling(template?.imageSrc, platform) ||
      socialIconUrl(platform);
    const node = {
      ...(template ? structuredClone(template) : { width: 28, height: 28 }),
      id: generateNodeId(),
      type: 'IMAGE' as const,
      parentId: socialRowId,
      name: label,
      imageSrc,
      imageAlt: label,
      linkUrl: socialLinks?.[platform] ?? '',
      children: undefined,
    } as SceneNode;
    s.addNewNode(node, socialRowId);
    setSocialAnchor(null);
  };

  return (
    <>
      <Paper
        elevation={4}
        onPointerDown={(e) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          left: screenLeft,
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          px: 0.5,
          py: 0.25,
          borderRadius: 2,
          zIndex: 5,
          ...(placeBelow
            ? { top: screenBottom + 12, transform: 'translate(-50%, 0)' }
            : { top: screenTop - GAP_ABOVE, transform: 'translate(-50%, -100%)' }),
        }}
      >
        <Tooltip title={t('selectionToolbar.duplicate', 'Duplicate')}>
          <IconButton size="small" onClick={act((s) => s.duplicateSelected())}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('selectionToolbar.bringForward', 'Bring forward')}>
          <IconButton size="small" onClick={act((s) => s.moveZOrder('forward'))}>
            <FlipToFrontIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('selectionToolbar.sendBackward', 'Send backward')}>
          <IconButton size="small" onClick={act((s) => s.moveZOrder('backward'))}>
            <FlipToBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canFloat && (
          <Tooltip
            title={
              isFloating
                ? t('selectionToolbar.dock', 'Dock back into layout')
                : t('selectionToolbar.float', 'Float freely (position over other elements)')
            }
          >
            <IconButton size="small" onClick={act((s) => s.toggleSelectedFloating())}>
              {isFloating ? (
                <VerticalAlignCenterIcon fontSize="small" />
              ) : (
                <OpenWithIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
        {(canGroup || isContainer) && (
          <Tooltip
            title={
              canGroup
                ? t('selectionToolbar.group', 'Group')
                : t('selectionToolbar.ungroup', 'Ungroup')
            }
          >
            <IconButton
              size="small"
              onClick={act((s) => (canGroup ? s.groupSelected() : s.ungroupSelected()))}
            >
              <GroupWorkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {isContainer && (single?.children?.length ?? 0) >= 2 && (
          <Tooltip
            title={t('selectionToolbar.flipLayout', 'Flip layout (mirror image/text arrangement)')}
          >
            <IconButton size="small" onClick={act((s) => s.flipSelectedLayout())}>
              <SwapHorizIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {productCardRootId && (
          <Tooltip title={t('selectionToolbar.replaceProduct', 'Replace product')}>
            <IconButton size="small" onClick={() => setProductPickerOpen(true)}>
              <ShoppingBagOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canGroup && (
          <Tooltip title={t('selectionToolbar.align', 'Align & distribute')}>
            <IconButton size="small" onClick={(e) => setAlignAnchor(e.currentTarget)}>
              <AlignHorizontalCenterIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {socialRowId && (
          <Tooltip title={t('selectionToolbar.addSocialIcon', 'Add social icon')}>
            <IconButton size="small" onClick={(e) => setSocialAnchor(e.currentTarget)}>
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
        <Tooltip title={t('selectionToolbar.delete', 'Delete')}>
          <IconButton size="small" color="error" onClick={act((s) => s.removeSelected())}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      <Popover
        open={!!alignAnchor}
        anchorEl={alignAnchor}
        onClose={() => setAlignAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {(
              [
                ['left', t('selectionToolbar.alignLeft', 'Align left'), AlignHorizontalLeftIcon],
                [
                  'hcenter',
                  t('selectionToolbar.alignHCenter', 'Align horizontal centers'),
                  AlignHorizontalCenterIcon,
                ],
                [
                  'right',
                  t('selectionToolbar.alignRight', 'Align right'),
                  AlignHorizontalRightIcon,
                ],
                ['top', t('selectionToolbar.alignTop', 'Align top'), AlignVerticalTopIcon],
                [
                  'vcenter',
                  t('selectionToolbar.alignVCenter', 'Align vertical centers'),
                  AlignVerticalCenterIcon,
                ],
                [
                  'bottom',
                  t('selectionToolbar.alignBottom', 'Align bottom'),
                  AlignVerticalBottomIcon,
                ],
              ] as [AlignEdge, string, typeof AlignHorizontalLeftIcon][]
            ).map(([edge, label, Icon]) => (
              <Tooltip key={edge} title={label}>
                <IconButton size="small" onClick={act((s) => s.alignSelected(edge))}>
                  <Icon fontSize="small" />
                </IconButton>
              </Tooltip>
            ))}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
            <Tooltip title={t('selectionToolbar.distributeH', 'Distribute horizontally')}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canDistribute}
                  onClick={act((s) => s.distributeSelected('horizontal'))}
                >
                  <ViewColumnIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('selectionToolbar.distributeV', 'Distribute vertically')}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canDistribute}
                  onClick={act((s) => s.distributeSelected('vertical'))}
                >
                  <ViewStreamIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('selectionToolbar.tidyUp', 'Tidy up')}>
              <IconButton size="small" onClick={act((s) => s.tidyUpSelected())}>
                <AutoFixHighIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Popover>

      <Popover
        open={!!socialAnchor}
        anchorEl={socialAnchor}
        onClose={() => setSocialAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box
          sx={{
            p: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 0.25,
            maxWidth: 220,
          }}
        >
          {[...SOCIAL_PLATFORMS]
            .sort((a, b) => Number(!!socialLinks?.[b.key]) - Number(!!socialLinks?.[a.key]))
            .map((p) => {
              const inBrand = !!socialLinks?.[p.key];
              return (
                <Tooltip
                  key={p.key}
                  title={
                    inBrand
                      ? p.label
                      : t('selectionToolbar.socialNotInBrand', '{{label}} (not in brand profile)', {
                          label: p.label,
                        })
                  }
                >
                  <IconButton
                    size="small"
                    sx={{ opacity: inBrand ? 1 : 0.4 }}
                    onClick={() => addSocialIcon(p.key as SocialPlatform, p.label)}
                  >
                    <Iconify icon={p.icon} width={22} height={22} color={p.color} />
                  </IconButton>
                </Tooltip>
              );
            })}
        </Box>
      </Popover>

      {productPickerOpen && (
        <ProductPickerModal
          open={productPickerOpen}
          onClose={() => setProductPickerOpen(false)}
          singleSelect
          selectedProducts={[]}
          onSelect={(products: ProductVariantSearchResultDto[]) => {
            setProductPickerOpen(false);
            const picked = products[0];
            if (!picked || !productCardRootId) return;
            // One undoable step rewrites image + title + price + compare-at +
            // links together, binding the card on first swap if it was unbound.
            const s = store.getState();
            s.applyNodePatches(
              buildProductSwapPatches(s.doc, productCardRootId, pickedProductFromDto(picked)),
            );
          }}
        />
      )}
    </>
  );
}
